import { FC } from 'react';
import { Button, ButtonStyles, Icons, SelectList, Table, TableColumn, TwoLinesCell } from '@silverfort/sf-components';
import { PredefinedTokenKey, TokenKey } from '../types';
import moment from 'moment';
import CopyIcon from '../../../../Controls/Buttons/CopyIcon';

const DATE_FORMAT = 'MMM D, YYYY';
const ROW_HEIGHT = 48;
const ROW_MARGIN = 8;

export type TokenRow = {
    id: TokenKey;
    name: string;
    lastModified: number;
    subtitle?: string;
    tenantId?: string;
    tenantName?: string;
    entraTokenId?: string;
    publicKey?: string;
    isExternal: boolean;
    isTestable: boolean;
};

interface Props {
    tableData: TokenRow[];
    handleEdit: (token: TokenRow) => void;
    handleDelete: (token: TokenRow) => void;
    handleCreate: () => void;
    onTest: (tokenId: PredefinedTokenKey, tenantName?: string) => void;
    allowTestToken: boolean;
}

const getKeyValue = (publicKey: string) => {
    const START_CERTIFICATE = '-----BEGIN CERTIFICATE-----';
    const END_CERTIFICATE = '-----END CERTIFICATE-----';

    return publicKey
        .replace(START_CERTIFICATE, '')
        .replace(END_CERTIFICATE, '')
        .replace(/\r?\n|\r/g, '');
};

const TokenTable: FC<Props> = ({ tableData, handleEdit, handleDelete, handleCreate, onTest, allowTestToken }) => {
    const columns: TableColumn<TokenRow>[] = [
        {
            columnKey: 'name',
            title: 'MFA provider',
            columnClassName: '!w-2/5',
            render: ({ name, subtitle }) => <TwoLinesCell topText={name} bottomText={subtitle || ''} />
        },
        {
            columnKey: 'lastModified',
            title: 'Last modified',
            columnClassName: '!w-3/5',
            render: (row) => {
                const newRowMenu = [
                    {
                        title: 'Edit',
                        onClick: (token) => handleEdit(token),
                        IconLeft: Icons.Edit
                    },
                    ...(allowTestToken && row.isTestable
                        ? [
                              {
                                  title: 'Test',
                                  onClick: (token) => onTest(token.id, token.tenantName),
                                  IconLeft: Icons.CircleCheck
                              }
                          ]
                        : []),
                    {
                        title: 'Remove',
                        onClick: (token) => handleDelete(token),
                        IconLeft: Icons.Delete
                    }
                ];
                return (
                    <div className="flex items-center flex-row align-middle justify-between">
                        <span>{row.lastModified ? moment(row.lastModified).format(DATE_FORMAT) : 'N/A'}</span>
                        <div className="flex items-center justify-end gap-2">
                            {row.publicKey && (
                                <CopyIcon
                                    value={getKeyValue(row.publicKey)}
                                    tooltipText="Copy public key"
                                    className="opacity-0 group-hover:opacity-100"
                                />
                            )}
                            <SelectList
                                menuProps={{
                                    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                                    transformOrigin: { vertical: 'top', horizontal: 'right' }
                                }}
                                items={
                                    newRowMenu.map((item) => ({
                                        ...item,
                                        value: row
                                    })) ?? []
                                }
                                id={`table`}
                                triggerProps={{
                                    iconLeft: Icons.MoreVertical,
                                    iconSize: 16,
                                    buttonStyle: ButtonStyles.GhostSolid
                                }}
                                TriggerComponent={Button}
                            />
                        </div>
                    </div>
                );
            }
        }
    ];

    return (
        <>
            <Table
                tableContainerClassName="w-full"
                columns={columns}
                rows={tableData}
                keyExtractor={({ name }) => name}
                loading={false}
                headerClassName="h-8"
                rowClassName="rounded-lg border"
                rowMargin={8}
                bodyHeight={tableData.length * ROW_HEIGHT + tableData.length * ROW_MARGIN}
            />
            <Button iconLeft={Icons.Add} text="Add Token" onClick={handleCreate} className="mt-2" />
        </>
    );
};

export default TokenTable;
